<template>
  <div class="modal-card" style="width: auto; max-width: 800px">
    <header class="modal-card-head">
      <p class="modal-card-title">{{ isEditing ? 'Editar Sócio' : 'Novo Sócio' }}</p>
      <button type="button" class="delete" @click="$emit('close')" />
    </header>
    <section class="modal-card-body">
      <form>
        <b-field
          label="Nome/Razão Social"
          :type="$v.form.nome.$error ? 'is-danger' : ''"
          :message="$v.form.nome.$error ? 'Campo obrigatório' : ''"
        >
          <b-input
            v-model="form.nome"
            placeholder="Nome completo ou Razão Social"
            class="is-uppercase"
            @input="form.nome = $event.toUpperCase()"
            @blur="$v.form.nome.$touch()"
          />
        </b-field>

        <b-field grouped class="column-direction-touch">
          <b-field
            expanded
            label="Documento"
            :type="$v.form.documento.$error ? 'is-danger' : ''"
            :message="
              $v.form.documento.$error
                ? $v.form.documento.required
                  ? 'Documento inválido'
                  : 'Campo obrigatório'
                : ''
            "
          >
            <b-input
              v-model="form.documento"
              placeholder="CPF ou CNPJ"
              v-mascara:documento="form.documento"
              @blur="$v.form.documento.$touch()"
            />
          </b-field>

          <b-field
            expanded
            label="Qualificação"
            :type="$v.form.qualificacao.$error ? 'is-danger' : ''"
            :message="$v.form.qualificacao.$error ? 'Campo obrigatório' : ''"
          >
            <b-select
              expanded
              v-model="form.qualificacao"
              placeholder="Selecione a qualificação"
              @input="onPartnerQualificacaoChange"
              @blur="$v.form.qualificacao.$touch()"
            >
              <option
                v-for="option in partnerQualifications"
                :key="option.codigo"
                :value="option.codigo"
              >
                {{ option.descricao }}
              </option>
            </b-select>
          </b-field>
        </b-field>

        <hr />
        <h3 class="title is-6">Representante Legal (opcional)</h3>

        <b-field label="Nome do Representante">
          <b-input
            v-model="form.representanteNome"
            placeholder="Nome completo"
            class="is-uppercase"
            @input="form.representanteNome = $event.toUpperCase()"
          />
        </b-field>

        <b-field grouped class="column-direction-touch">
          <b-field expanded label="CPF Representante">
            <b-input v-model="form.representanteCpf" placeholder="CPF" v-mascara:cpf />
          </b-field>

          <b-field
            expanded
            label="Qualificação Representante"
            :type="
              $v.form.representanteQualificacao.$error || $v.form.representanteCpf.$error
                ? 'is-danger'
                : ''
            "
            :message="
              $v.form.representanteCpf.$error
                ? 'CPF inválido'
                : $v.form.representanteQualificacao.$error
                ? 'Campo obrigatório'
                : ''
            "
          >
            <b-select
              expanded
              v-model="form.representanteQualificacao"
              placeholder="Selecione a qualificação"
              @input="onRepresentativeQualificacaoChange"
              @blur="$v.form.representanteQualificacao.$touch()"
            >
              <option
                v-for="option in representativeQualifications"
                :key="option.codigo"
                :value="option.codigo"
              >
                {{ option.descricao }}
              </option>
            </b-select>
          </b-field>
        </b-field>
      </form>
    </section>
    <footer class="modal-card-foot">
      <button class="button" type="button" @click="$emit('close')">Cancelar</button>
      <button class="button is-primary" @click="save">Salvar</button>
    </footer>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { isValidCpf } from '@brazilian-utils/validators';
import { documentValidator } from '@/utils/functions';

const cpfValidator = value => {
  if (!value) return true;
  const clean = value.replace(/\D/g, '');
  return isValidCpf(clean);
};

export default {
  name: 'PartnerManagementModal',
  props: {
    partner: {
      type: Object,
      default: () => ({
        nome: '',
        documento: '',
        qualificacao: '',
        qualificacaoDescricao: '',
        representanteNome: '',
        representanteCpf: '',
        representanteQualificacao: '',
        representanteQualificacaoDescricao: '',
      }),
    },
    index: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      form: { ...this.partner },
    };
  },
  validations: {
    form: {
      nome: { required },
      documento: { required, documentValidator },
      qualificacao: { required },
      representanteCpf: { cpfValidator },
      representanteQualificacao: {
        required: requiredIf(function isRepresentanteRequired() {
          return !!(this.form.representanteNome || this.form.representanteCpf);
        }),
      },
    },
  },
  computed: {
    ...mapState('companies', ['partnerQualifications', 'representativeQualifications']),
    isEditing() {
      return this.index !== null;
    },
  },
  async mounted() {
    if (this.partnerQualifications.length === 0) {
      await this.fetchPartnerQualifications();
    }
    if (this.representativeQualifications.length === 0) {
      await this.fetchRepresentativeQualifications();
    }
  },
  methods: {
    ...mapActions('companies', ['fetchPartnerQualifications', 'fetchRepresentativeQualifications']),
    onPartnerQualificacaoChange(value) {
      const selected = this.partnerQualifications.find(q => q.codigo === value);
      this.form.qualificacaoDescricao = selected ? selected.descricao : '';
    },
    onRepresentativeQualificacaoChange(value) {
      const selected = this.representativeQualifications.find(q => q.codigo === value);
      this.form.representanteQualificacaoDescricao = selected ? selected.descricao : '';
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      this.$emit('save', {
        index: this.index,
        partner: { ...this.form },
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-card {
  margin: 0 auto;
}

.is-uppercase >>> input {
  text-transform: uppercase;
}
</style>
